import { useState, useEffect, useRef } from 'react'

export default function PWAInstallPrompt() {
  const [deferredPrompt, setDeferredPrompt] = useState(null)
  const [showBanner, setShowBanner] = useState(false)
  const [showSteps, setShowSteps] = useState(false)
  const [isIOS, setIsIOS] = useState(false)
  const [isInstalled, setIsInstalled] = useState(false)
  const fallbackTimerRef = useRef(null)

  useEffect(() => {
    if (
      window.matchMedia('(display-mode: standalone)').matches ||
      window.navigator.standalone === true
    ) {
      setIsInstalled(true)
      return
    }

    if (localStorage.getItem('pwa-install-dismissed')) return

    const ios = /iphone|ipad|ipod/i.test(navigator.userAgent) && !window.MSStream
    setIsIOS(ios)

    const handler = (e) => {
      e.preventDefault()
      setDeferredPrompt(e)
      clearTimeout(fallbackTimerRef.current)
      setTimeout(() => setShowBanner(true), 3000)
    }

    window.addEventListener('beforeinstallprompt', handler)
    window.addEventListener('appinstalled', () => {
      setIsInstalled(true)
      setShowBanner(false)
    })

    fallbackTimerRef.current = setTimeout(() => setShowBanner(true), 3000)

    return () => {
      window.removeEventListener('beforeinstallprompt', handler)
      clearTimeout(fallbackTimerRef.current)
    }
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  const handleInstall = async () => {
    if (deferredPrompt) {
      // Native one-tap install
      deferredPrompt.prompt()
      const { outcome } = await deferredPrompt.userChoice
      if (outcome === 'accepted') {
        setIsInstalled(true)
        setShowBanner(false)
      }
      setDeferredPrompt(null)
    } else {
      // Show manual steps inline
      setShowSteps(true)
    }
  }

  const handleDismiss = () => {
    setShowBanner(false)
    localStorage.setItem('pwa-install-dismissed', '1')
  }

  if (!showBanner || isInstalled) return null

  return (
    <div className="fixed bottom-4 left-4 right-4 z-[9999] sm:left-auto sm:right-4 sm:w-96">
      <div className="bg-white rounded-2xl shadow-2xl border border-gray-100 overflow-hidden">

        {/* Header */}
        <div className="bg-gradient-to-r from-green-600 to-green-700 px-5 py-4 flex items-center gap-3">
          <img src="/icons/icon-72x72.png" alt="UCH" className="w-10 h-10 rounded-xl" />
          <div>
            <p className="text-white font-bold text-sm">UCH Self Service</p>
            <p className="text-green-100 text-xs">Install on your device</p>
          </div>
          <button
            onClick={handleDismiss}
            className="ml-auto text-green-100 hover:text-white text-xl leading-none"
            aria-label="Close"
          >
            ✕
          </button>
        </div>

        {/* Body */}
        <div className="px-5 py-4">
          {!showSteps ? (
            <>
              <p className="text-gray-700 text-sm mb-4">
                Add <strong>UCH Self Service</strong> to your home screen for instant access — pay for hospital services anytime, even offline.
              </p>
              <div className="flex gap-3">
                <button
                  onClick={handleDismiss}
                  className="flex-1 py-2.5 rounded-xl border border-gray-200 text-gray-600 text-sm font-medium hover:bg-gray-50 transition"
                >
                  Not Now
                </button>
                <button
                  onClick={handleInstall}
                  className="flex-1 py-2.5 rounded-xl bg-green-600 hover:bg-green-700 text-white text-sm font-semibold transition"
                >
                  Install App
                </button>
              </div>
            </>
          ) : isIOS ? (
            <>
              <p className="text-gray-700 text-sm font-medium mb-3">Follow these steps in Safari:</p>
              <ol className="text-gray-600 text-sm space-y-2">
                <li className="flex items-center gap-2">
                  <span className="w-5 h-5 bg-green-100 text-green-700 rounded-full flex items-center justify-center text-xs font-bold shrink-0">1</span>
                  Tap the <strong className="mx-1">Share</strong> <span className="text-blue-500">⎙</span> button
                </li>
                <li className="flex items-center gap-2">
                  <span className="w-5 h-5 bg-green-100 text-green-700 rounded-full flex items-center justify-center text-xs font-bold shrink-0">2</span>
                  Tap <strong className="ml-1">Add to Home Screen</strong>
                </li>
                <li className="flex items-center gap-2">
                  <span className="w-5 h-5 bg-green-100 text-green-700 rounded-full flex items-center justify-center text-xs font-bold shrink-0">3</span>
                  Tap <strong className="ml-1">Add</strong> to confirm
                </li>
              </ol>
              <button
                onClick={handleDismiss}
                className="mt-4 w-full py-2.5 rounded-xl border border-gray-200 text-gray-600 text-sm font-medium hover:bg-gray-50 transition"
              >
                Done
              </button>
            </>
          ) : (
            <>
              <p className="text-gray-700 text-sm font-medium mb-3">Follow these steps in your browser:</p>
              <ol className="text-gray-600 text-sm space-y-2">
                <li className="flex items-start gap-2">
                  <span className="w-5 h-5 bg-green-100 text-green-700 rounded-full flex items-center justify-center text-xs font-bold shrink-0 mt-0.5">1</span>
                  Tap the <strong className="mx-1">⋮ menu</strong> (top right of your browser)
                </li>
                <li className="flex items-start gap-2">
                  <span className="w-5 h-5 bg-green-100 text-green-700 rounded-full flex items-center justify-center text-xs font-bold shrink-0 mt-0.5">2</span>
                  Select <strong className="ml-1">Add to Home Screen</strong> or <strong className="ml-1">Install App</strong>
                </li>
                <li className="flex items-start gap-2">
                  <span className="w-5 h-5 bg-green-100 text-green-700 rounded-full flex items-center justify-center text-xs font-bold shrink-0 mt-0.5">3</span>
                  Tap <strong className="ml-1">Install</strong> to confirm
                </li>
              </ol>
              <button
                onClick={handleDismiss}
                className="mt-4 w-full py-2.5 rounded-xl border border-gray-200 text-gray-600 text-sm font-medium hover:bg-gray-50 transition"
              >
                Done
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  )
}
